<template>
  <div class="content mb-5">
    <div class="mb-3">
      {{ $t("Customise the user interface to fit your brand.") }}
    </div>

    <b-row>
      <b-col cols="12" md="6">
        <h4 v-if="!loading" class="mt-5 mb-3">{{ $t("Company Details") }}</h4>
        <b-skeleton
          class="mt-5 mb-3"
          v-if="loading"
          animation="wave"
          height="32px"
          width="30%"
        ></b-skeleton>

        <b-form-group
          v-if="!loading"
          label-for="companyName"
          class="mb-0"
          :label="this.$t('Description')"
          :state="$validateState($v.form.companyName)"
        >
          <b-form-input
            id="companyName"
            data-test-id="interface-company-name"
            v-model="form.companyName"
            @blur="setValidationInput('companyName', $event.target.value)"
            :state="$validateState($v.form.companyName)"
            :placeholder="this.$t('e.g. Marketing')"
          />

          <template #invalid-feedback>
            <p v-if="!$v.form.companyName.required">
              {{ $t("This is a required field.") }}
            </p>
          </template>
        </b-form-group>
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="30%"
        ></b-skeleton>
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="100%"
        ></b-skeleton>
        <p class="mt-1 mb-6" v-if="!loading">
          {{
            $t(
              "This will be displayed in search engine results, browser tabs and more."
            )
          }}
        </p>
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="90%"
        ></b-skeleton>

        <b-form-group
          :label="this.$t('Your Company Logo')"
          label-for="company-logo"
          v-if="!loading"
        >
          <div class="logo mt-2 mb-2">
            <img class="logo-image" :src="form.logo" />
          </div>
          <p>Supports .png (800 &times; 200) | max size 1MB</p>
          <b-form-file
            data-test-id="interface-logo"
            plain
            class="d-none"
            id="company-logo-input"
            ref="file-image"
            v-model="form.logoFile"
            accept="image/png"
            variant="secondary"
            :state="Boolean(form.logoFile)"
            @change="uploadLogo"
          ></b-form-file>
          <b-button
            variant="outline-secondary"
            type="button"
            onclick="document.getElementById('company-logo-input').click()"
          >
            {{ $t("Choose a file") }}
          </b-button>
        </b-form-group>
        <b-skeleton
          class="mt-5 mb-3"
          v-if="loading"
          animation="wave"
          height="286px"
          width="100%"
        ></b-skeleton>
        <b-skeleton
          class="mt-5 mb-3"
          v-if="loading"
          animation="wave"
          height="32px"
          width="50%"
        ></b-skeleton>
        <b-skeleton
          class="mt-5 mb-3"
          v-if="loading"
          animation="wave"
          height="32px"
          width="30%"
        ></b-skeleton>
      </b-col>
    </b-row>
    <t-save-button
      data-test-id="interface-save"
      :form-model="$v.form"
      :fetching="fetching"
      @submit="onSubmit"
    />
  </div>
</template>

<route>
{
"name": "System Interface Settings"
}
</route>

<script>
import { mapActions } from "vuex";
import { required } from "vuelidate/lib/validators";

export default {
  methods: {
    ...mapActions("pageMeta", ["setPageTitle"]),
    ...mapActions("toast", ["displayToast"]),
    setValidationInput(object, value) {
      this.form[`${object}`] = value;
      this.$v.form[`${object}`].$touch();
    },
    uploadLogo(event) {
      try {
        const image = event.target.files[0];
        const reader = new FileReader();
        reader.readAsDataURL(image);
        reader.onload = (event) => {
          const maxFileSize = 1048576;
          const file = this.$refs["file-image"].files[0];
          if (file.size > maxFileSize) {
            this.displayToast({
              title: this.$t("Error"),
              message: this.$t("File size excedes maximum of 1mb."),
              duration: 2000,
              variant: "danger",
            });
            throw this.$t("File size excedes maximum of 1mb.");
          }
          this.form.logo = event.target.result;
        };
        this.$v.$touch();
      } catch (err) {
        this.displayToast({
          title: this.$t("Error"),
          message: this.$t("Something went wrong."),
          duration: 2000,
          variant: "danger",
        });
      }
    },
    onSubmit() {
      this.fetching = true;
      try {
        this.displayToast({
          title: this.$t("Error"),
          message: "Missing endpoint",
          duration: 2000,
          variant: "danger",
        });
      } finally {
        this.fetching = false;
      }
    },
  },
  data() {
    return {
      loading: true,
      fetching: false,
      form: {
        logo: require("@/assets/branding/Titan_Logo.svg"),
        companyName: "TitanHQ",
        logoFile: [],
      },
    };
  },
  validations: {
    form: {
      companyName: {
        required,
      },
      logoFile: {},
      themes: {},
    },
  },
  async created() {
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
    } finally {
      this.loading = false;
    }
  },
  mounted() {
    this.setPageTitle(this.$t("Interface Settings"));
  },
};
</script>

<style lang="scss" scoped>
$dark: #dfdfdf;
$light: #e6e6e6;

.logo {
  .logo-image {
    padding: 3rem;
    width: 100%;
    background-repeat: repeat;
    background-size: 1.4rem 1.4rem;
    background-position: 0 0, 0 0, -0.7rem -0.7rem, 0.7rem 0.7rem;
    background-image: linear-gradient(45deg, $dark 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, $dark 75%),
      linear-gradient(45deg, transparent 75%, $dark 75%),
      linear-gradient(45deg, $dark 25%, $light 25%);
  }
}
</style>
